// Task #6144 — disabled Reserve/Pay button UX + telemetry.
//
// A real renter stalled 5 times at checkout because the pay button was gray
// (unverified phone) and the reason was easy to miss. This module derives a
// single "blocking gate" descriptor from the existing disable conditions so:
//   1. tapping the disabled button scrolls to + highlights the blocking step,
//   2. analytics can record WHICH gate is blocking (checkout_pay_blocked /
//      checkout_pay_blocked_tap).
//
// Only USER-ACTIONABLE gates resolve to a reason. Transient gates (quote
// updating, tax recalculating, feature flags loading, booking in flight)
// return null — the button just stays disabled with its existing status text.
// The gating logic itself is unchanged; this is a read-only projection of it.

export type PayBlockedReason =
  | "primary_driver_choice"
  | "primary_driver_email"
  | "occasion_required"
  | "payment_currency_unresolved"
  | "quote_stalled"
  | "phone_unverified"
  | "message_blocked"
  | "delivery_out_of_range";

/** The form rule is shared by the visible Reserve explanation and its submit guard. */
export function resolveDriverChoiceIssue(
  required: boolean,
  choice: boolean | undefined,
  alternateEmail: string,
  renterEmail: string,
): "choice" | "email" | "same_email" | null {
  if (!required) return null;
  if (choice === undefined) return "choice";
  if (choice === true) return null;
  const email = alternateEmail.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "email";
  return email === renterEmail.trim().toLowerCase() ? "same_email" : null;
}

/** DOM id each reason scrolls to. The phone/message sections live in
 *  CheckoutGuestSection; the delivery error alert + pay-button anchor live in
 *  CheckoutPaymentSection. */
export const PAY_BLOCKER_TARGET_IDS: Record<PayBlockedReason, string> = {
  primary_driver_choice: "checkout-primary-driver-choice",
  primary_driver_email: "checkout-primary-driver-email",
  occasion_required: "checkout-occasion-section",
  phone_unverified: "checkout-phone-verification-section",
  message_blocked: "checkout-message-to-host-section",
  delivery_out_of_range: "checkout-delivery-range-error",
  // No dedicated "currency" section — scroll to the pay button whose helper
  // text ("We couldn't prepare your payment…") explains the fix.
  payment_currency_unresolved: "checkout-pay-button-anchor",
  // Task #8483 — same idea: no dedicated section, the retry affordance
  // lives inline in the price-details panel and the pay button's own
  // helper text; scroll to the pay button so both are in view.
  quote_stalled: "checkout-pay-button-anchor",
};

/**
 * Resolve the user-actionable gate currently blocking the pay button, or null
 * when the button is enabled or only transiently disabled. Priority mirrors
 * the helper-text priority in CheckoutPaymentSection (currency → quote
 * stalled → phone → message) so the scroll target always matches the helper
 * copy shown.
 *
 * Task #8483 — `quoteStalled` (a price refresh that hit the client's own
 * stall watchdog rather than a genuinely transient in-flight fetch) is
 * intentionally included here, unlike `updatingQuote`/`taxRecalcInFlight`:
 * those mean "still working, wait a moment" (nothing for the renter to do),
 * while a stall means "gave up, retry" — a real user-actionable gate with
 * its own affordance, so it earns a tracked reason + scroll target like the
 * other entries in this list.
 */
export function resolvePayBlockedReason(gates: {
  chargeCurrencyMissing?: boolean;
  quoteStalled?: boolean;
  phoneGateBlocking?: boolean;
  payNoteBlocking?: boolean;
  deliveryRangeError?: string | null;
  driverChoiceBlockMessage?: string | null;
  primaryDriverChoice?: boolean;
  occasionRequired?: boolean;
}): PayBlockedReason | null {
  if (gates.chargeCurrencyMissing) return "payment_currency_unresolved";
  if (gates.quoteStalled) return "quote_stalled";
  if (gates.phoneGateBlocking) return "phone_unverified";
  if (gates.payNoteBlocking) return "message_blocked";
  if (gates.deliveryRangeError) return "delivery_out_of_range";
  if (gates.driverChoiceBlockMessage) return gates.primaryDriverChoice === false ? "primary_driver_email" : "primary_driver_choice";
  if (gates.occasionRequired) return "occasion_required";
  return null;
}

// ring-destructive (red) — allowed outside the green brand-token guard.
const HIGHLIGHT_CLASSES = [
  "ring-2",
  "ring-destructive/70",
  "ring-offset-2",
  "transition-shadow",
];
const HIGHLIGHT_MS = 2000;

/**
 * Smooth-scroll the blocking section into view and flash a brief red ring so
 * the renter's eye lands on the step to complete. jsdom-safe: scrollIntoView
 * is guarded, and a missing target is a silent no-op.
 */
export function revealPayBlocker(reason: PayBlockedReason): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById(PAY_BLOCKER_TARGET_IDS[reason]);
  if (!el) return;
  if (typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  // Focus the actual input (or the first occasion option), not the Reserve button.
  const focusTarget = reason === "occasion_required"
    ? el.querySelector<HTMLElement>("button[data-testid^='occasion-tile-']")
    : reason === "primary_driver_choice"
      ? el.querySelector<HTMLElement>("input[type='radio']")
      : el.matches("input, textarea, button") ? el : el.querySelector<HTMLElement>("input, textarea, button");
  (focusTarget as HTMLElement | null)?.focus({ preventScroll: true });
  for (const c of HIGHLIGHT_CLASSES) el.classList.add(c);
  window.setTimeout(() => {
    for (const c of HIGHLIGHT_CLASSES) el.classList.remove(c);
  }, HIGHLIGHT_MS);
}

/** Fire-and-forget analytics capture (respects the consent gating inside
 *  @/lib/analytics — track() is a no-op until consent is granted). */
export function trackPayBlocked(
  event: "checkout_pay_blocked" | "checkout_pay_blocked_tap",
  props: { reason: PayBlockedReason; car_id?: number; rental_type?: string },
): void {
  void import("@/lib/analytics").then(({ track }) => {
    track(event, props);
  });
}
